import React from 'react'
import { useFormContext } from 'react-hook-form'
import { FieldErrorMessage, FormItem, RequiredIndicator } from '~/components'
import { StyledCheckbox } from './StyledCheckbox'
import { formGridCell } from './FormFieldCell'
import { cn } from '~/lib/utils'

// Full literal class strings — see the same map in StyledRadioGroup. Building
// `2xl:${...}` by interpolation only worked because `2xl:grid-cols-3` happens to
// be written out literally elsewhere, so Tailwind emitted it.
const GRID_COLUMNS_MAP: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-2 2xl:grid-cols-3',
}

export type StyledCheckboxFieldProps = {
  name: string
  label: string
  options: ReadonlyArray<{ label: string; value: string; description?: string }>
  className?: string
  maxColumns?: number
  required?: boolean
  error?: { message?: string } | null
  onCheckedChange?: (value: string, checked: boolean) => void
}

export const StyledCheckboxField = ({
  name,
  label,
  options,
  className,
  maxColumns = 2,
  required,
  error,
  onCheckedChange: onCheckedChangeProp,
}: StyledCheckboxFieldProps) => {
  const form = useFormContext()
  const fieldsetId = `checkbox-group-${label.toLowerCase().replace(/\s+/g, '-')}`
  const hasError =
    error !== undefined
      ? !!error?.message
      : !!form.getFieldState(name, form.formState).error?.message

  return (
    // The shared two-row grid cell (see `formGridCell`) — same as StyledRadioGroup,
    // including why the fieldset is `contents`.
    <FormItem className={cn(formGridCell, 'gap-y-4')}>
      <fieldset className="contents">
        <legend
          id={`${fieldsetId}-legend`}
          className={cn(
            'text-sm font-medium',
            hasError ? 'text-red-500 dark:text-red-900' : 'text-heading'
          )}
        >
          {label}
          {required && <RequiredIndicator />}
        </legend>
        <div>
          <div
            className={cn('grid', GRID_COLUMNS_MAP[maxColumns] || 'grid-cols-2', 'gap-3 lg:gap-6')}
            role="group"
            aria-labelledby={`${fieldsetId}-legend`}
          >
            {options.map((option, index) => {
              const fieldName = `${name}.${option.value}`
              return (
                <StyledCheckbox
                  key={option.value}
                  label={option.label}
                  description={option.description}
                  checked={form.watch(fieldName) ?? false}
                  onCheckedChange={(checked) => {
                    form.setValue(fieldName, checked, { shouldValidate: false })
                    if (onCheckedChangeProp) {
                      onCheckedChangeProp(option.value, !!checked)
                    } else {
                      form.trigger(fieldName)
                      const parentState = form.getFieldState(name, form.formState)
                      if (parentState.error?.message) {
                        form.trigger(name)
                      }
                    }
                  }}
                  id={`${fieldsetId}-option-${index}`}
                />
              )
            })}
          </div>
          {/* Callers that hand in an `error` used to render this themselves in a wrapper <div>,
              which broke the grid cell in two. Owning it here — as StyledRadioGroup already does —
              keeps the group a single cell. */}
          {error !== undefined && <FieldErrorMessage fieldState={{ error: error ?? undefined }} />}
        </div>
      </fieldset>
    </FormItem>
  )
}
